import AddCircleIcon from '@mui/icons-material/AddCircle';
import HistoryIcon from '@mui/icons-material/History';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Button, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useCustomerId, useSpaData, useSubmittalId } from '../../../../hooks';
import useShowAddRequest from '../../../../hooks/useShowAddRequest';
import { useGetFeedbacksListQuery } from '../../../../services/feedbackQueries';
import AddRequestDialog from '../../../AddRequestDialog/AddRequestDialog';
import Summary from '../FeedbackSummary/Summary';
import FeedbacksList from '../FeedbacksList';

const classes = {
	feedbacksPaper: {
		display: 'flex',
		width: '100%',
		flexDirection: 'row',
		justifyContent: 'center',
		// eslint-disable-next-line no-unused-vars
		padding: (theme) => ({
			xs: '0',
			md: '0',
			lg: '8px',
		}),
		backgroundColor: (theme) => theme.palette.background.default,
	},
	feedbacksWidth: {
		width: { xs: '100%', sm: '66%' },
	},
};

const initialState = {
	feedbacks: [],
	summary: {
		allRatingsAverage: 0,
		ratings: [],
		recommendations: [],
		recommendationStatus: {},
		noData: false,
		formDetails: [],
	},
};

function Feedbacks() {
	const { t } = useTranslation('candidate-feedback');

	const { addFeedbackRequestLabel, feedbackSummary, viewAll, auditHistory, feedback } = useMemo(
		() => ({
			addFeedbackRequestLabel: t('add_feedback_request'),
			feedbackSummary: t('feedback_summary'),
			viewAll: t('view_all_feedback'),
			auditHistory: t('audit_history'),
			feedback: t('individual_feedback'),
		}),
		[t],
	);
	const [disableControls, setDisableControls] = useState(false);

	const submittalId = useSubmittalId();
	const customerId = useCustomerId();
	const navigate = useNavigate();

	const {
		loggedInUser: loggedInUserId = -1,
		hideAddFeedbackRequestButton,
		hideNoRatingsAndRecommendations,
		auditHistoryForInterviewFeedbackEnabled,
	} = useSpaData();

	const {
		data: feedbacks = initialState,
		isPending,
		isError,
		refetch,
	} = useGetFeedbacksListQuery({
		submittalId,
		customerId,
		loggedInUserId
	});

	const feedbackData = feedbacks.feedbacks;

	useEffect(() => {
		const disable = isPending || isError;
		setDisableControls(disable);
	}, [isError, isPending, setDisableControls]);

	const [, setShowAddRequest] = useShowAddRequest();

	const handleAddFeedbackRequest = useCallback(() => {
		setShowAddRequest(true);
	}, [setShowAddRequest]);

	const handleFeedbackSummaryRequest = () =>
		navigate(`/proxy/feedback/dashboard/workflow/feedback/summary/${submittalId}`);

	return (
		<>
			<Box
				display="flex"
				flexDirection="column"
				width="100%"
				height="100%"
				tabIndex="-1"
				data-testid="feedbacks-container"
				sx={{ overflowY: 'auto', overflowX: 'hidden' }}
			>
				<Box display="flex" flex={1} width="100%" flexDirection="column" alignItems="center">
					<Box
						sx={classes.feedbacksWidth}
						display="flex"
						flexDirection="row"
						position="relative"
						flex={1}
					>
						<Box position="absolute" top={0} right={0} bottom={0} left={0} p={{ xs: 1 }}>
							{!isError && (
								<Box>
									<Box
										width={{ xs: '100%', sm: '100%', lg: '100%', xl: '100%' }}
										display="flex"
										flexDirection="row"
										justifyContent="space-between"
									>
										<Box>
											{ !(feedbacks?.summary?.noData && hideNoRatingsAndRecommendations) && <Typography variant="h2">{feedbackSummary}</Typography>}
										</Box>
										<Box sx={{ display: 'flex', gap: 1 }}>
											{auditHistoryForInterviewFeedbackEnabled && <Button
												aria-label={auditHistory}
												variant="text"
												startIcon={<HistoryIcon />}
												data-testid="audit-history-button"
											>
												{auditHistory}
											</Button>}
											<Button
												aria-label={viewAll}
												variant="contained"
												startIcon={<VisibilityIcon />}
												onClick={handleFeedbackSummaryRequest}
												disabled={
													feedbacks.summary.formDetails.length === 0 ||
													feedback.summary?.hideSummary === true ||
													disableControls
												}
												data-testid="view-detailed-summary"
											>
												{viewAll}
											</Button>
										</Box>
									</Box>
									<Summary
										feedbacksSummary={feedbacks.summary}
										isSummaryPending={isPending}
										noData={feedbacks.summary.noData}
									/>
								</Box>
							)}
							<Box
							    mt={2}
								width={{ xs: '100%', sm: '100%', lg: '100%', xl: '100%' }}
								display="flex"
								flexDirection="row"
								justifyContent="space-between"
							>
								<Box>
									<Typography variant="h2">{feedback}</Typography>
								</Box>
								<Box>
									{!hideAddFeedbackRequestButton && (
										<Button
											aria-label={addFeedbackRequestLabel}
											color="primary"
											startIcon={<AddCircleIcon />}
											onClick={handleAddFeedbackRequest}
											disabled={disableControls}
										>
											{addFeedbackRequestLabel}
										</Button>
									)}
								</Box>
							</Box>

							<FeedbacksList
								refetch={refetch}
								feedbacksList={feedbackData}
								loading={isPending}
								error={isError}
							/>
						</Box>
					</Box>
				</Box>
			</Box>
			<AddRequestDialog />
		</>
	);
}

export default Feedbacks;
